import React, { useCallback, useEffect, useRef, useState } from "react";
import { getInitiativeOrchestration, executeInitiativeCommand, normalizeError } from "./api";
import "./InitiativeGuidancePanel.css";

/**
 * Where this initiative is, why, and what should happen next.
 *
 * Everything shown here comes from the server's snapshot. This component
 * deliberately derives nothing: no client-side readiness, no locally computed
 * blockers, no reordering of the recommendation. The reason is that guidance the
 * browser invents can disagree with what the server will actually allow, and a
 * customer who is told to do something the API then refuses has been misled by
 * the product rather than blocked by a control.
 */

/** Customer-facing phase names. The API's enum values never reach the screen. */
const PHASE_LABELS = {
  capturing_request: "Capturing the request",
  clarifying_decisions: "Clarifying decisions",
  agreeing_handoff: "Agreeing the handoff",
  implementing: "Being built",
  collecting_evidence: "Gathering evidence",
  awaiting_decision: "Waiting for a decision",
  decided: "Decided",
  merge_ready: "Cleared to merge",
  returned_for_rework: "Returned for rework",
};

/**
 * What is true now, which is a different question from how far the work got.
 * Shown next to the phase because the phase alone made an initiative that reached
 * acceptance and then hit a contradiction read as finished.
 */
const EFFECTIVE_STATE_LABELS = {
  not_started: "Not started yet",
  progressing: "In progress, nothing blocked",
  blocked_on_product: "Blocked — product has to decide",
  blocked_on_engineering: "Blocked — engineering has to act",
  blocked_on_review: "Blocked — waiting on the review",
  blocked_on_administration: "Blocked — needs an administrator",
  cleared_to_merge: "Cleared to merge",
};

const GUIDANCE_BUCKET_BY_STATE = {
  blocked_on_product: "product",
  blocked_on_engineering: "engineering",
  blocked_on_review: "reviewer",
  blocked_on_administration: "administrator",
};

const GUIDANCE_HEADINGS = {
  product: "What product has to decide",
  engineering: "What engineering has to do",
  reviewer: "What the reviewer needs to know",
  administrator: "What an administrator has to set up",
};

const GATE_LABELS = {
  discovery_ready: "Decisions settled",
  engineering_handoff_ready: "Handoff agreed",
  implementation_review_ready: "Ready to review",
  human_acceptance_ready: "Ready for a decision",
  merge_control_ready: "Cleared to merge",
};

const GATE_RESULT_LABELS = {
  passed: "Done",
  failed: "Blocked",
  not_applicable: "Not yet",
  // Deliberately not "Unknown — assume fine". An unverifiable gate is a real
  // blocker and the wording has to carry that.
  unverifiable: "Cannot confirm",
};

const ROLE_LABELS = {
  product: "Product",
  engineering: "Engineering",
  reviewer: "Reviewer",
  control_owner: "Administrator",
  none: "No one",
};

const TREND_LABELS = {
  improving: "Closer to done than last checked",
  unchanged: "No change since last checked",
  regressed: "Further from done than last checked",
  unknown: null,
};

function roleLabel(role) {
  return ROLE_LABELS[role] || "Someone";
}

function GateRow({ gateId, gate }) {
  const label = GATE_LABELS[gateId] || gateId;
  const resultLabel = GATE_RESULT_LABELS[gate.result] || gate.result;
  return (
    <li
      className={`initiative-gate initiative-gate-${gate.result}`}
      data-testid={`initiative-gate-${gateId}`}
    >
      <span className="initiative-gate-name">{label}</span>
      <span className="initiative-gate-result">{resultLabel}</span>
      {gate.result === "failed" || gate.result === "unverifiable" ? (
        <span className="initiative-gate-owner">{roleLabel(gate.responsibleRole)}</span>
      ) : null}
    </li>
  );
}

function findingIdsFromSnapshot(snapshot) {
  const ids = [];
  const gates = snapshot?.gates?.gates || {};
  Object.values(gates).forEach((gate) => {
    (gate.contradictions || []).forEach((raw) => {
      const match = /^intent_contradiction:(.+)$/.exec(String(raw));
      if (match && match[1] && !ids.includes(match[1])) ids.push(match[1]);
    });
  });
  return ids;
}

const CONTRADICTION_RESOLUTIONS = [
  {
    id: "not_a_contradiction",
    label: "These do not actually conflict",
    hint: "The requirements can both hold. Nothing about them changes.",
  },
  {
    id: "accepted_as_intended",
    label: "Both are intended",
    hint: "The tension is real and accepted, with a reason on the record.",
  },
  {
    id: "deferred",
    label: "Defer this decision",
    hint: "Set it aside deliberately so work can continue, with a reason.",
  },
];

function CommandStatus({ status, message }) {
  if (!status) return null;
  return (
    <p
      className={`initiative-command-status initiative-command-${status}`}
      data-testid="initiative-command-status"
      role="status"
    >
      {message}
    </p>
  );
}

function ContradictionCommand({ featurePlanId, snapshot, onCompleted, onSnapshot }) {
  const [resolution, setResolution] = useState("not_a_contradiction");
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const submittingRef = useRef(false);

  const submit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || busy) return;
    if (!String(rationale || "").trim()) {
      setStatus("rejected");
      setMessage("Say why this is the right decision. A conflict cannot be settled silently.");
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    setStatus("submitting");
    setMessage("Recording the decision…");
    try {
      const findingIds = findingIdsFromSnapshot(snapshot);
      const result = await executeInitiativeCommand(featurePlanId, {
        actionId: "resolve_intent_contradiction",
        idempotencyKey:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `cmd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        expectedFingerprint: snapshot.fingerprint,
        input: {
          findingId: findingIds[0],
          resolution,
          rationale: rationale.trim(),
        },
      });
      setStatus(result.receipt?.outcome === "reconciliation_required" ? "reconciliation" : "completed");
      setMessage(
        result.receipt?.outcome === "reconciliation_required"
          ? "The decision was recorded, but the orchestration receipt could not be audited. Refresh to confirm."
          : "Recorded. The conflict no longer blocks this version."
      );
      onCompleted(
        result.snapshot,
        result.receipt?.outcome === "reconciliation_required"
          ? "The decision was recorded, but the orchestration receipt could not be audited. Refresh to confirm."
          : "Recorded. The conflict no longer blocks this version."
      );
    } catch (err) {
      const normalized = normalizeError(err);
      if (normalized.outcome === "stale_snapshot" || normalized.code === "STALE_SNAPSHOT") {
        setStatus("stale");
        setMessage("This recommendation is out of date. Refresh and try again.");
        if (normalized.snapshot) onCompleted(normalized.snapshot);
      } else if (normalized.outcome === "reconciliation_required") {
        setStatus("reconciliation");
        setMessage(normalized.message || "The decision may have been recorded. Refresh to confirm.");
      } else {
        setStatus(normalized.outcome || "failed");
        setMessage(normalized.message || "The decision could not be recorded.");
      }
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <form className="initiative-command" data-testid="initiative-command" onSubmit={submit}>
      <fieldset disabled={busy}>
        <legend>Settle the conflict</legend>
        {CONTRADICTION_RESOLUTIONS.map((option) => (
          <label key={option.id} className="initiative-command-option">
            <input
              type="radio"
              name="contradiction-resolution"
              value={option.id}
              checked={resolution === option.id}
              onChange={() => setResolution(option.id)}
            />
            <span>
              <strong>{option.label}</strong>
              <span className="initiative-command-hint">{option.hint}</span>
            </span>
          </label>
        ))}
        <label className="initiative-command-rationale">
          Why this decision
          <textarea
            data-testid="initiative-command-rationale"
            value={rationale}
            onChange={(event) => setRationale(event.target.value)}
            rows={3}
            maxLength={2000}
            required
          />
        </label>
      </fieldset>
      <button
        type="submit"
        className="initiative-command-submit"
        data-testid="initiative-command-submit"
        disabled={busy || !snapshot?.fingerprint}
      >
        {busy ? "Recording…" : "Record this decision"}
      </button>
      <CommandStatus status={status} message={message} />
      {status === "stale" ? (
        <button
          type="button"
          className="initiative-command-refresh"
          data-testid="initiative-command-refresh"
          onClick={onSnapshot}
        >
          Refresh status
        </button>
      ) : null}
    </form>
  );
}

function NextAction({ action, featurePlanId, snapshot, onCompleted, onRefresh }) {
  if (!action) return null;
  return (
    <div className="initiative-next-action" data-testid="initiative-next-action">
      <h6>Next: {action.explanation}</h6>
      <dl>
        <div>
          <dt>Who</dt>
          <dd>{roleLabel(action.responsibleRole)}</dd>
        </div>
        <div>
          <dt>Why now</dt>
          <dd>{action.rationale}</dd>
        </div>
        <div>
          <dt>Afterwards</dt>
          <dd>{action.expectedResult}</dd>
        </div>
      </dl>
      {action.execution === "manual" ? (
        <p className="initiative-manual-note">
          This is done outside DataFlow. Nothing here can do it for you.
        </p>
      ) : null}
      {action.executable && action.actionId === "resolve_intent_contradiction" ? (
        <ContradictionCommand
          featurePlanId={featurePlanId}
          snapshot={snapshot}
          onCompleted={onCompleted}
          onSnapshot={onRefresh}
        />
      ) : action.execution === "api" && !action.executable ? (
        <p className="initiative-manual-note">
          This still has to be done on its own screen. Orchestration can recommend it but cannot carry it out yet.
        </p>
      ) : null}
    </div>
  );
}

export default function InitiativeGuidancePanel({ featurePlanId, refreshKey, onSnapshot }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [commandNotice, setCommandNotice] = useState(null);
  const statusRef = useRef(null);

  // Kept in a ref so a caller passing an inline function does not restart the
  // fetch on every render.
  const onSnapshotRef = useRef(onSnapshot);
  useEffect(() => {
    onSnapshotRef.current = onSnapshot;
  }, [onSnapshot]);

  const load = useCallback(async () => {
    if (!featurePlanId) return;
    setLoading(true);
    setError(null);
    setDenied(false);
    try {
      const data = await getInitiativeOrchestration(featurePlanId);
      setSnapshot(data);
      // Reported upward so other surfaces can defer to the server's answer instead
      // of deriving a second, possibly contradictory one.
      if (typeof onSnapshotRef.current === "function") onSnapshotRef.current(data);
    } catch (err) {
      const normalized = normalizeError(err);
      if (normalized.status === 404 || normalized.status === 403) {
        setDenied(true);
      } else {
        setError(normalized.message || "Could not load the current status.");
      }
      setSnapshot(null);
      // Null rather than a stale snapshot: a consumer must not keep deferring to an
      // answer the server is no longer giving.
      if (typeof onSnapshotRef.current === "function") onSnapshotRef.current(null);
    } finally {
      setLoading(false);
    }
  }, [featurePlanId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!featurePlanId) return null;

  if (loading && !snapshot) {
    return (
      <section className="initiative-guidance" aria-labelledby="initiative-guidance-heading">
        <h5 id="initiative-guidance-heading">Where this stands</h5>
        <p className="initiative-status" role="status">
          Working out where this initiative stands…
        </p>
      </section>
    );
  }

  if (denied) {
    return (
      <section className="initiative-guidance" aria-labelledby="initiative-guidance-heading">
        <h5 id="initiative-guidance-heading">Where this stands</h5>
        <p className="initiative-status initiative-denied" role="status">
          You do not have access to this initiative.
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="initiative-guidance" aria-labelledby="initiative-guidance-heading">
        <h5 id="initiative-guidance-heading">Where this stands</h5>
        <p className="initiative-status initiative-error" role="alert">
          {error}
        </p>
        <button type="button" onClick={load}>
          Try again
        </button>
      </section>
    );
  }

  if (!snapshot) {
    return (
      <section className="initiative-guidance" aria-labelledby="initiative-guidance-heading">
        <h5 id="initiative-guidance-heading">Where this stands</h5>
        <p className="initiative-status">Nothing to show for this initiative yet.</p>
      </section>
    );
  }

  const furthestPhase = snapshot.furthestPhaseReached || snapshot.phase;
  const phaseLabel = PHASE_LABELS[furthestPhase] || furthestPhase;
  const trend = TREND_LABELS[snapshot.readinessTrend];
  const gateEntries = Object.entries(snapshot.gates?.gates || {});

  const effectiveState = snapshot.currentEffectiveState || null;
  const effectiveLabel = effectiveState ? EFFECTIVE_STATE_LABELS[effectiveState] : null;
  const blockedBucket = effectiveState ? GUIDANCE_BUCKET_BY_STATE[effectiveState] : null;
  // Only the side that has to move gets its guidance opened. The rest is available
  // but collapsed: four lists of advice at once reads as noise and buries the one
  // that applies.
  const ownerGuidance = blockedBucket ? snapshot.guidance?.[blockedBucket] || [] : [];
  const staleFromLabel = snapshot.staleFromPhase
    ? PHASE_LABELS[snapshot.staleFromPhase] || snapshot.staleFromPhase
    : null;

  return (
    <section className="initiative-guidance" aria-labelledby="initiative-guidance-heading">
      <h5 id="initiative-guidance-heading">Where this stands</h5>

      {/* Announced on change so a screen reader learns the phase moved without
          the user hunting for it. */}
      <p className="initiative-phase" role="status" ref={statusRef}>
        <strong>{phaseLabel}.</strong> {snapshot.phaseExplanation}
      </p>

      {effectiveLabel ? (
        <p
          className={`initiative-effective-state initiative-effective-${effectiveState}`}
          data-testid="initiative-effective-state"
          role="status"
        >
          <strong>Right now: {effectiveLabel}.</strong>{" "}
          {blockedBucket
            ? "Reaching an earlier step does not make it safe to carry on while this stands."
            : null}
        </p>
      ) : null}

      {staleFromLabel ? (
        <p className="initiative-warning" data-testid="initiative-stale-from" role="status">
          Work from “{staleFromLabel}” onwards has to be re-established. What was already
          recorded stays on the record; it just no longer describes the current version.
        </p>
      ) : null}

      {snapshot.superseded ? (
        <p className="initiative-warning" role="status">
          A newer version of this work has replaced what is shown here. Anything decided
          against this version stays on the record but no longer describes the current work.
        </p>
      ) : snapshot.stale ? (
        <p className="initiative-warning" role="status">
          The code or the requirements have moved since this was last checked, so the
          findings below describe an earlier state.
        </p>
      ) : null}

      {trend ? <p className="initiative-trend">{trend}</p> : null}

      <NextAction
        action={snapshot.nextAction}
        featurePlanId={featurePlanId}
        snapshot={snapshot}
        onCompleted={(next, notice) => {
          if (notice) setCommandNotice(notice);
          if (next) {
            setSnapshot(next);
            if (typeof onSnapshotRef.current === "function") onSnapshotRef.current(next);
          } else {
            load();
          }
        }}
        onRefresh={load}
      />
      {commandNotice ? (
        <p className="initiative-command-status initiative-command-completed" data-testid="initiative-command-status" role="status">
          {commandNotice}
        </p>
      ) : null}

      {!snapshot.nextAction && (snapshot.blockingReasons || []).length === 0 ? (
        <p className="initiative-status" data-testid="initiative-no-action">
          Nothing is waiting on anyone for this version.
        </p>
      ) : null}

      {(snapshot.blockingReasons || []).length > 0 ? (
        <div className="initiative-blockers">
          <h6>What is holding this up</h6>
          <ul>
            {snapshot.blockingReasons.map((reason) => (
              <li key={`${reason.gateId}-${reason.code}`}>
                {reason.detail} <span className="initiative-owner">{roleLabel(reason.owner)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {ownerGuidance.length > 0 ? (
        <div className="initiative-guidance-detail" data-testid="initiative-owner-guidance">
          <h6>{GUIDANCE_HEADINGS[blockedBucket]}</h6>
          <ul>
            {ownerGuidance.map((line, index) => (
              // Guidance lines are server-authored prose with no id of their own;
              // they are re-rendered wholesale on every fetch, so the index is
              // stable for as long as the list is.
              <li key={`${blockedBucket}-${index}`}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="initiative-gates">
        <h6>Progress</h6>
        {/* No percentage. A count of passed gates would imply the remaining ones
            are equal in size, and they are not. */}
        <ul>
          {gateEntries.map(([gateId, gate]) => (
            <GateRow key={gateId} gateId={gateId} gate={gate} />
          ))}
        </ul>
      </div>

      {(snapshot.feedbackDestinations || []).length > 0 ? (
        <div className="initiative-feedback">
          <h6>Where the gaps go next</h6>
          <ul>
            {snapshot.feedbackDestinations.map((destination) => (
              <li key={destination.owner}>
                <strong>{roleLabel(destination.owner)}</strong>
                {destination.requirementCount > 0
                  ? ` — ${destination.requirementCount} requirement(s)`
                  : ""}
                {destination.summary ? `: ${destination.summary}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {(snapshot.prohibitedActions || []).length > 0 ? (
        <details className="initiative-prohibited">
          <summary>What cannot be done right now</summary>
          <ul>
            {snapshot.prohibitedActions.map((action) => (
              <li key={action.actionId}>{action.reason}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {(snapshot.alternativeActions || []).length > 0 ? (
        <details className="initiative-alternatives">
          <summary>Other things that would help</summary>
          <ul>
            {snapshot.alternativeActions.map((action) => (
              <li key={action.actionId}>
                {action.explanation}{" "}
                <span className="initiative-owner">{roleLabel(action.responsibleRole)}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <p className="initiative-provenance">
        Assessed under {snapshot.policyId}. A review recommends; only a person accepts.
      </p>
    </section>
  );
}
